import { useMutation, useQuery } from '@apollo/client';
import { faChevronDown, faChevronRight, faThumbsDown, faThumbsUp } from '@fortawesome/pro-regular-svg-icons';
import { format } from 'date-fns';
import { camelCase } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../../../core/components/Button.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import Modal from '../../../../core/components/Modal';
import classnames from '../../../../core/utils/classnames';
import { GET_AGENT_PROCESSING_RECORD, RATE_AGENT_PROCESSING_RECORD } from '../../queries.ts';

interface ProcessingRecordModalProps {
    visible: boolean;
    onClose: () => void;
    record: any;
    isTesting?: boolean;
}

export const parseJsonSafe = (data: any) => {
    if (typeof data === 'string') {
        try {
            return JSON.parse(data);
        } catch (e) {
            return null;
        }
    }
    return data;
};

const ExpandIcon = withIcon(faChevronRight);
const CollapseIcon = withIcon(faChevronDown);
const ThumbsUpIcon = withIcon(faThumbsUp);
const ThumbsDownIcon = withIcon(faThumbsDown);

const ExpandableCard = ({ title, children }: { title: React.ReactNode; children: React.ReactNode }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="bg-surface-medium border border-line-weak rounded-lg w-full flex flex-col overflow-hidden">
            <button
                className={classnames(
                    'text-sm font-semibold text-content-strong flex items-center gap-2 p-4 hover:bg-surface-strong cursor-pointer',
                    isExpanded ? 'border-b border-line-weak' : ''
                )}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <span className="text-sm opacity-70 text-center w-4">
                    {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                </span>
                <span>{title}</span>
            </button>

            {isExpanded && <div className="p-4 flex flex-col gap-4">{children}</div>}
        </div>
    );
};

const TraceStep = ({ step }: { step: any }) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const data = step?.data;
    const isToolContext = step?.step_type === 'tool_context';

    return (
        <div className="bg-surface-medium border border-line-weak rounded-md w-full p-3 py-2.5 text-sm text-content-strong flex flex-col gap-1">
            <time className="text-xs text-content-medium flex gap-1 items-center">
                <span>{t(`recordModal.stepTypes.${camelCase(step?.step_type || '')}`)}</span>
                <span>-</span>
                <span>{step?.timestamp ? format(new Date(step.timestamp), 'PPpp') : '-'}</span>
            </time>
            {isToolContext && data ? (
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                    {Object.entries(data).map(([key, value]) => (
                        <span key={key} className="text-sm">
                            <span className="text-content-medium">{key}:</span> <span>{String(value)}</span>
                        </span>
                    ))}
                </div>
            ) : (
                <p>{step?.message || '-'}</p>
            )}
        </div>
    );
};

interface RatingSectionProps {
    recordId: string;
    userRatingHelpful: boolean | null;
    userRatingAt: string | null;
    userRatingBy: { id: string; email: string; firstName: string; lastName: string } | null;
    userRatingReasons: string[] | string | null;
    userRatingFeedback: string | null;
}

const RatingSection = ({
    recordId,
    userRatingHelpful,
    userRatingAt,
    userRatingBy,
    userRatingReasons,
    userRatingFeedback,
}: RatingSectionProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const [rateRecord, { loading }] = useMutation(RATE_AGENT_PROCESSING_RECORD);

    const handleRate = async (helpful: boolean) => {
        await rateRecord({
            // make it possible to reset a rating by clicking the same button again
            variables: { recordId, helpful: helpful === userRatingHelpful ? null : helpful },
        });
    };

    const ratedByName = userRatingBy
        ? userRatingBy.firstName && userRatingBy.lastName
            ? `${userRatingBy.firstName} ${userRatingBy.lastName}`
            : userRatingBy.email
        : null;

    return (
        <div className="bg-surface-medium border border-line-weak rounded-lg w-full p-3 px-4 rounded-t-none border-t-0">
            <div className="flex items-center">
                <div className="flex items-center gap-3">
                    <span className="text-sm text-content-medium">{t('recordModal.ratingQuestion')}</span>
                    <div className="flex items-center gap-1">
                        <Button
                            variant="outline"
                            disabled
                            className={classnames(
                                'h-8 w-8 flex items-center justify-center rounded-md transition-colors shadow-none',
                                userRatingHelpful === true
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'text-content-medium',
                                userRatingHelpful === false && 'opacity-50'
                            )}
                            title={t('recordModal.ratingHelpful')}
                        >
                            <ThumbsUpIcon />
                        </Button>
                        <Button
                            variant="outline"
                            disabled
                            className={classnames(
                                'h-8 w-8 flex items-center justify-center rounded-md transition-colors shadow-none',
                                userRatingHelpful === false
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                    : 'text-content-medium',
                                userRatingHelpful === true && 'opacity-50'
                            )}
                            title={t('recordModal.ratingNotHelpful')}
                        >
                            <ThumbsDownIcon />
                        </Button>
                    </div>
                </div>

                {userRatingHelpful !== null && userRatingAt && ratedByName && (
                    <span className="ml-2 text-sm text-content-medium">
                        {t('recordModal.ratedBy', {
                            name: ratedByName,
                            date: format(new Date(userRatingAt), 'PPp'),
                        })}
                    </span>
                )}
            </div>

            {(() => {
                const reasons: string[] | null = parseJsonSafe(userRatingReasons);
                return Array.isArray(reasons) && reasons.length > 0 ? (
                    <div className="mt-3 pt-2 flex flex-col gap-1">
                        <span className="text-xs text-content-medium">{t('recordModal.ratingReasonsLabel')}</span>
                        <div className="flex flex-wrap gap-1.5">
                            {reasons.map((reason, i) => (
                                <span
                                    key={i}
                                    className="text-xs px-2 py-0.5 rounded-full bg-surface-strong text-content-strong"
                                >
                                    {t(`recordModal.ratingReasons.${reason}`, { defaultValue: reason })}
                                </span>
                            ))}
                        </div>
                    </div>
                ) : null;
            })()}

            {userRatingFeedback && (
                <div className="mt-3 pt-2 flex flex-col gap-1">
                    <span className="text-xs text-content-medium">{t('recordModal.ratingFeedbackLabel')}</span>
                    <p className="text-sm text-content-strong">{userRatingFeedback}</p>
                </div>
            )}
        </div>
    );
};

const getSkillResultStatusInfo = (status: string | undefined, t: any) => {
    switch (status) {
        case 'success':
            return {
                label: t('recordModal.skillStatus.success'),
                colorClass: 'bg-surface-success-weak text-content-success-strong border-line-success-strong',
            };
        case 'not_found':
            return {
                label: t('recordModal.skillStatus.notFound'),
                colorClass: 'bg-surface-warning-weak text-content-warning-strong border-line-warning-strong',
            };
        case 'error':
            return {
                label: t('recordModal.skillStatus.error'),
                colorClass: 'bg-surface-danger-weak text-content-danger-strong border-line-danger-strong',
            };
        case 'partial':
            return {
                label: t('recordModal.skillStatus.partial'),
                colorClass: 'bg-surface-warning-weak text-content-warning-strong border-line-warning-strong',
            };
        default:
            return null;
    }
};

const SkillResultCard = ({ result }: { result: any }) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });

    const skillKey = result?.skill_key || '';
    const translationKey = camelCase(skillKey).replace('Skill', '');
    const confidence = result?.confidence;
    const reasoning = result?.reasoning;
    const data = result?.data;
    const status = result?.status;
    const errorMessage = result?.error_message;

    const statusInfo = getSkillResultStatusInfo(status, t);

    const titleContent = (
        <span className="flex items-center gap-2">
            <span>{t(`skills.${translationKey}.name`, { defaultValue: skillKey })}</span>
            {statusInfo && (
                <span
                    className={classnames(
                        'inline-flex rounded text-xs font-medium items-center px-2 py-0.5 border border-solid',
                        statusInfo.colorClass
                    )}
                >
                    {statusInfo.label}
                </span>
            )}
        </span>
    );

    return (
        <ExpandableCard title={titleContent}>
            {status === 'error' && errorMessage && (
                <div className="bg-surface-danger-weak border border-line-danger-strong rounded-md p-3">
                    <h5 className="text-xs font-medium text-content-danger-strong mb-1">
                        {t('recordModal.errorMessageLabel')}
                    </h5>
                    <p className="text-sm text-content-danger-strong">{errorMessage}</p>
                </div>
            )}

            {confidence != null && (
                <div>
                    <h5 className="text-xs font-medium text-content-medium mb-1">{t('recordModal.confidenceLabel')}</h5>
                    <span className="text-sm text-content-strong">{Math.round(confidence * 100)}%</span>
                </div>
            )}

            {reasoning && (
                <div>
                    <h5 className="text-xs font-medium text-content-medium mb-1">{t('recordModal.reasoningLabel')}</h5>
                    <p className="text-sm text-content-strong">{reasoning}</p>
                </div>
            )}

            <div>
                <h5 className="text-xs font-medium text-content-medium mb-1">{t('recordModal.outputDataSection')}</h5>
                <pre className="text-xs text-content-strong whitespace-pre-wrap break-words">
                    {data ? JSON.stringify(data, null, 2) : '-'}
                </pre>
            </div>
        </ExpandableCard>
    );
};

const isSkillResultsArray = (data: any): boolean => {
    return Array.isArray(data) && data.length > 0 && data[0]?.skill_key !== undefined;
};
const ProcessingRecordModal = ({ visible, onClose, record, isTesting = false }: ProcessingRecordModalProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const { data: fullRecordData } = useQuery(GET_AGENT_PROCESSING_RECORD, {
        variables: { id: record?.id },
        skip: !record?.id,
    });
    const fullRecord = fullRecordData?.agentProcessingRecord;

    if (!fullRecord) return null;

    const inputData = parseJsonSafe(fullRecord.inputData);
    const outputData = parseJsonSafe(fullRecord.outputData);
    const traceData = parseJsonSafe(fullRecord.trace) || {};

    const isFailed = record?.stepRun?.executionStatus === 'FAILED';
    const errorData = isFailed ? JSON.parse(fullRecord.stepRun?.error) : null;
    const traceback = errorData ? errorData.traceback?.replace(/\\n/g, '\n') : null;

    return (
        <Modal visible={visible} onClose={onClose} title={t('recordModal.title')} className="max-w-3xl w-full">
            <div className="flex flex-col gap-6 pb-6">
                <div>
                    <h3 className="text-sm font-semibold text-content-strong mb-2">
                        {t('recordModal.inputDataSection')}
                    </h3>

                    <ExpandableCard title={t('recordModal.emailTitle')}>
                        <table>
                            <tbody>
                                <tr>
                                    <td className="text-sm font-medium text-content-medium py-1 pr-2">
                                        {t('recordModal.dateLabel')}
                                    </td>
                                    <td className="text-sm text-content-strong">{inputData?.date || '-'}</td>
                                </tr>
                                <tr>
                                    <td className="text-sm font-medium text-content-medium py-1 pr-2">
                                        {t('recordModal.messageIdLabel')}
                                    </td>
                                    <td className="text-sm text-content-strong break-all">
                                        {inputData?.messageId || inputData?.message_id || '-'}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="text-sm font-medium text-content-medium py-1 pr-2">
                                        {t('recordModal.senderLabel')}
                                    </td>
                                    <td className="text-sm text-content-strong">{inputData?.sender || '-'}</td>
                                </tr>
                                <tr>
                                    <td className="text-sm font-medium text-content-medium py-1 pr-2">
                                        {t('recordModal.receiversLabel')}
                                    </td>
                                    <td className="text-sm text-content-strong">
                                        {inputData?.receivers ? inputData.receivers.join(', ') : '-'}
                                    </td>
                                </tr>
                                <tr>
                                    <td className="text-sm font-medium text-content-medium py-1 pr-2">
                                        {t('recordModal.subjectLabel')}
                                    </td>
                                    <td className="text-sm text-content-strong">{inputData?.subject || '-'}</td>
                                </tr>
                            </tbody>
                        </table>

                        <hr className="border-line-weak w-full block" />

                        <p className="text-sm text-content-strong whitespace-pre-wrap break-words">
                            {inputData?.body || '-'}
                        </p>
                    </ExpandableCard>
                </div>

                {isFailed ? (
                    <div>
                        <h3 className="text-sm font-semibold text-content-strong mb-2">
                            {t('recordModal.failedSection')}
                        </h3>
                        <div className="bg-surface-medium border border-line-weak rounded-lg w-full p-4">
                            <pre className="text-xs text-content-strong whitespace-pre-wrap break-words overflow-auto max-h-96">
                                {traceback}
                            </pre>
                        </div>
                    </div>
                ) : isSkillResultsArray(outputData) ? (
                    <div>
                        <h3 className="text-sm font-semibold text-content-strong mb-2">
                            {t('recordModal.extractedIntents')}
                        </h3>
                        <div className="flex flex-col gap-4">
                            {outputData.map((result: any, index: number) => (
                                <SkillResultCard key={index} result={result} />
                            ))}
                        </div>
                    </div>
                ) : (
                    <div>
                        <h3 className="text-sm font-semibold text-content-strong mb-2">
                            {t('recordModal.outputDataSection')}
                        </h3>

                        <div className="bg-surface-medium border border-line-weak rounded-lg w-full p-4">
                            {outputData ? (
                                <pre className="text-xs text-content-strong whitespace-pre-wrap break-words overflow-auto max-h-96">
                                    {JSON.stringify(outputData, null, 2)}
                                </pre>
                            ) : (
                                <span className="text-sm text-content-medium">{t('recordModal.noOutputData')}</span>
                            )}
                        </div>
                    </div>
                )}

                <div>
                    <h3 className="text-sm font-semibold text-content-strong mb-2">
                        {t('recordModal.outputTextSection')}
                    </h3>

                    <div className="bg-surface-medium border border-line-weak rounded-lg w-full p-4 rounded-b-none">
                        {fullRecord.outputText ? (
                            <p className="text-sm text-content-strong whitespace-pre-wrap break-words">
                                {fullRecord.outputText}
                            </p>
                        ) : (
                            <span className="text-sm text-content-medium">{t('recordModal.noOutputText')}</span>
                        )}
                    </div>

                    {!isTesting && (
                        <RatingSection
                            recordId={fullRecord.id}
                            userRatingHelpful={fullRecord.userRatingHelpful}
                            userRatingAt={fullRecord.userRatingAt}
                            userRatingBy={fullRecord.userRatingBy}
                            userRatingReasons={fullRecord.userRatingReasons}
                            userRatingFeedback={fullRecord.userRatingFeedback}
                        />
                    )}
                </div>

                <div>
                    <h3 className="text-sm font-semibold text-content-strong mb-2">{t('recordModal.traceSection')}</h3>
                    <div>
                        {traceData?.steps && traceData.steps.length > 0 ? (
                            <ExpandableCard title={t('recordModal.traceStepsTitle', { count: traceData.steps.length })}>
                                {traceData.steps.map((step: any, index: number) => (
                                    <TraceStep key={index} step={step} />
                                ))}
                            </ExpandableCard>
                        ) : (
                            <div className="bg-surface-medium border border-line-weak rounded-lg w-full p-4">
                                <span className="text-sm text-content-medium">{t('recordModal.noTraceSteps')}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ProcessingRecordModal;
